import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../api/client';
import { useSession } from './SessionContext';
import { useAuth } from './AuthContext';

const ConversationContext = createContext(null);
const STORAGE_KEY = 'etch.conversation';

export function ConversationProvider({ children }) {
  const { workspacePath, sessionMode, sessionId, selectedPersona } = useSession();
  // The server scopes the history to the caller (migration 010), so a sign-in
  // or sign-out changes the answer and the list has to be fetched again.
  const { user } = useAuth();
  const [currentId, setCurrentIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const refreshTimer = useRef(null);

  const setCurrentId = useCallback((id) => {
    setCurrentIdState(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  const refresh = useCallback(async () => {
    if (!workspacePath && sessionMode !== 'axiom' && sessionMode !== 'catalog') return;
    setLoading(true);
    try {
      const { conversations: list } = await apiClient.listConversations({
        workspace: workspacePath || undefined,
        search: searchQuery || undefined,
      });
      setConversations(list || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [workspacePath, sessionMode, searchQuery, user?.id]);

  // Load on workspace/session changes
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Debounced search refresh
  useEffect(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => {
      refresh();
    }, 250);
    return () => clearTimeout(refreshTimer.current);
  }, [searchQuery]);

  const createConversation = useCallback(async (title = null) => {
    const { conversation } = await apiClient.createConversation({
      workspacePath: workspacePath || null,
      mode: sessionMode,
      title,
      personaId: selectedPersona?.id || null,
      personaName: selectedPersona?.name || null,
    });
    setCurrentId(conversation.id);
    setConversations(prev => [conversation, ...prev]);
    return conversation;
  }, [workspacePath, sessionMode, selectedPersona, setCurrentId]);

  const renameConversation = useCallback(async (id, title) => {
    const { conversation } = await apiClient.renameConversation(id, title);
    setConversations(prev => prev.map(c => c.id === id ? conversation : c));
    return conversation;
  }, []);

  const deleteConversation = useCallback(async (id) => {
    await apiClient.deleteConversation(id);
    setConversations(prev => prev.filter(c => c.id !== id));
    if (currentId === id) setCurrentId(null);
  }, [currentId, setCurrentId]);

  const requestAutoTitle = useCallback(async (id) => {
    if (!sessionId) return null;
    try {
      const { title } = await apiClient.autoTitleConversation(id, sessionId);
      setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
      return title;
    } catch {
      return null;
    }
  }, [sessionId]);

  const newConversation = useCallback(() => {
    setCurrentId(null);
  }, [setCurrentId]);

  const value = {
    currentId,
    setCurrentId,
    conversations,
    loading,
    searchQuery,
    setSearchQuery,
    refresh,
    createConversation,
    renameConversation,
    deleteConversation,
    requestAutoTitle,
    newConversation,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

export function useConversations() {
  const ctx = useContext(ConversationContext);
  if (!ctx) throw new Error('useConversations must be used within ConversationProvider');
  return ctx;
}

export default ConversationContext;
